import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link, useParams } from "react-router-dom";
import { formatNumber, ratingColor } from "@/lib/format";
import { Star, Buildings, Globe, EnvelopeSimple } from "@phosphor-icons/react";
import { AppsTable, SectionTitle } from "@/components/AppTable";

export function DevelopersList() {
  const [devs, setDevs] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/developers", { params: q ? { q } : {} }).then(r => setDevs(r.data));
  }, [q]);

  return (
    <div className="p-8 space-y-6">
      <SectionTitle eyebrow="Portfolio" title="Developers" />
      <input data-testid="dev-search" value={q} onChange={(e) => setQ(e.target.value)}
             placeholder="Search developers"
             className="w-full max-w-sm border border-input bg-background px-3 py-2 text-sm rounded-sm" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 grid-borders border-l border-t border-border">
        {devs.map((d) => (
          <Link key={d.developer_id} to={`/developers/${encodeURIComponent(d.developer_id)}`}
                className="p-5 hover:bg-accent transition-colors" data-testid={`dev-card-${d.developer_id}`}>
            <div className="flex items-center gap-2 mb-2">
              <Buildings size={16} className="text-primary" />
              <div className="font-heading font-bold text-base truncate">{d.name}</div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs mt-4">
              <div>
                <div className="overline-label text-muted-foreground">Apps</div>
                <div className="font-mono font-bold text-lg">{d.app_count}</div>
              </div>
              <div>
                <div className="overline-label text-muted-foreground">Rating</div>
                <div className={`font-mono font-bold text-lg ${ratingColor(d.avg_rating)}`}>{d.avg_rating?.toFixed(1) || "—"}</div>
              </div>
              <div>
                <div className="overline-label text-muted-foreground">Reviews</div>
                <div className="font-mono font-bold text-lg">{formatNumber(d.total_reviews)}</div>
              </div>
            </div>
            {(d.low_rated_count > 0 || d.new_app_count > 0) && (
              <div className="flex gap-1.5 mt-3">
                {d.new_app_count > 0 && <span className="text-[10px] px-1.5 py-0.5 border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 rounded-sm">{d.new_app_count} new</span>}
                {d.low_rated_count > 0 && <span className="text-[10px] px-1.5 py-0.5 border border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400 rounded-sm">{d.low_rated_count} low-rated</span>}
              </div>
            )}
          </Link>
        ))}
        {!devs.length && <div className="col-span-full p-10 text-center text-sm text-muted-foreground">No developers yet.</div>}
      </div>
    </div>
  );
}

export function DeveloperDetail() {
  const { id } = useParams();
  const [dev, setDev] = useState(null);

  useEffect(() => {
    api.get(`/developers/${encodeURIComponent(id)}`).then(r => setDev(r.data)).catch(() => {});
  }, [id]);

  if (!dev) return <div className="p-10">Loading…</div>;

  return (
    <div className="p-8 space-y-6 max-w-6xl">
      <div>
        <div className="overline-label text-muted-foreground">Developer</div>
        <h1 className="font-heading font-black text-4xl mt-1">{dev.name}</h1>
        <div className="flex flex-wrap gap-4 mt-3 text-sm">
          {dev.website && <a href={dev.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline"><Globe size={14} />{dev.website}</a>}
          {dev.email && <a href={`mailto:${dev.email}`} className="inline-flex items-center gap-1 hover:underline"><EnvelopeSimple size={14} />{dev.email}</a>}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 grid-borders border-l border-t border-border">
        {[
          ["Apps Tracked", dev.app_count],
          ["Total Installs", formatNumber(dev.total_installs)],
          ["Avg Rating", dev.avg_rating?.toFixed(2) || "—"],
          ["Total Reviews", formatNumber(dev.total_reviews)],
          ["Categories", dev.categories?.length || 0],
        ].map(([l, v]) => (
          <div key={l} className="p-4">
            <div className="overline-label text-muted-foreground">{l}</div>
            <div className="font-heading font-black text-xl mt-1">{v}</div>
          </div>
        ))}
      </div>

      <section>
        <SectionTitle eyebrow="Portfolio" title="Apps" />
        <AppsTable apps={dev.apps || []} />
      </section>
    </div>
  );
}
